import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useApiProblem } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { useDebounce } from '@/shared/hooks/useDebounce';

const PAGE_SIZE = 50;

/**
 * Aktive Kunden-Workspaces für den Umschalter. Die Plattformliste antwortet nur der Heimatsitzung,
 * deshalb liest sie `homeApi` — auch während einer Support-Sitzung.
 */
export function useSwitcherWorkspaces(enabled: boolean) {
  const { homeApi } = useAuth();
  const [search, setSearch] = useState('');
  const term = useDebounce(search.trim(), 300);
  const query = useQuery({
    queryKey: ['support-session', 'workspaces', term],
    queryFn: () => homeApi.workspaces.list({ search: term || undefined, pageSize: PAGE_SIZE }),
    enabled,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
  const problem = useApiProblem(query.error);
  const items = query.data?.items ?? [];

  return {
    search,
    setSearch,
    term,
    workspaces: items.filter((workspace) => workspace.kind === 'Customer' && workspace.isActive),
    isLoading: query.isLoading,
    isError: query.isError,
    problem,
    retry: () => void query.refetch(),
    /** Die Seite ist voll: weitere Treffer gibt es nur mit einer engeren Suche. */
    more: (query.data?.total ?? 0) > items.length,
  };
}

export type SwitcherWorkspaces = ReturnType<typeof useSwitcherWorkspaces>;
